import { useState } from 'react';

import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { createWalletRecharge, createWalletWithdrawal } from '../../services/walletService';
import { transferParrainageWallet } from '../../services/parrainageService';
import { formatMoney } from '../../utils/currency';

type Props = {
  walletSolde: number;
  walletDevise: string;
  transferableBonusCdf?: number;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  showRecharge?: boolean;
  showTransfer?: boolean;
  withdrawReason?: string;
  heading?: string;
  subtitle?: string;
};

export default function WalletActionsPanel({
  walletSolde,
  walletDevise,
  transferableBonusCdf,
  onSuccess,
  onError,
  showRecharge = true,
  showTransfer = true,
  withdrawReason = 'Retrait portefeuille',
  heading = 'Utiliser votre portefeuille',
  subtitle,
}: Props) {
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawPhone, setWithdrawPhone] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferReferralCode, setTransferReferralCode] = useState('');
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [rechargePhone, setRechargePhone] = useState('');
  const [busy, setBusy] = useState<'withdraw' | 'transfer' | 'recharge' | null>(null);

  const transferable =
    typeof transferableBonusCdf === 'number' ? transferableBonusCdf : walletSolde;

  const handleWithdraw = async () => {
    setBusy('withdraw');
    onError('');
    try {
      await createWalletWithdrawal({
        amount: Number(withdrawAmount),
        channel: 'MOBILE_MONEY',
        destinationAccount: withdrawPhone.trim(),
        reason: withdrawReason,
      });
      setWithdrawAmount('');
      setWithdrawPhone('');
      onSuccess('Demande de retrait enregistrée. Vous serez notifié après traitement.');
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Retrait impossible');
    } finally {
      setBusy(null);
    }
  };

  const handleTransfer = async () => {
    setBusy('transfer');
    onError('');
    const code = transferReferralCode.trim().toUpperCase();
    const amount = Number(transferAmount);
    try {
      await transferParrainageWallet({
        destinataireCodeParrainage: code,
        montant: amount,
      });
      setTransferAmount('');
      setTransferReferralCode('');
      onSuccess(`Transfert de ${formatMoney(amount, walletDevise)} effectué vers le code ${code}.`);
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Transfert impossible');
    } finally {
      setBusy(null);
    }
  };

  const handleRecharge = async () => {
    setBusy('recharge');
    onError('');
    try {
      await createWalletRecharge({
        amount: Number(rechargeAmount),
        channel: 'MOBILE_MONEY',
        sourceAccount: rechargePhone.trim(),
      });
      setRechargeAmount('');
      onSuccess('Demande de recharge initiée. Confirmez le paiement Mobile Money sur votre téléphone.');
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Recharge impossible');
    } finally {
      setBusy(null);
    }
  };

  const actionCount = (showRecharge ? 1 : 0) + 1 + (showTransfer ? 1 : 0);
  const defaultSubtitle = showTransfer
    ? `Bonus transférable : ${formatMoney(transferable, walletDevise)} — retrait Mobile Money ou transfert interne par code parrainage.`
    : showRecharge
      ? `Solde disponible : ${formatMoney(walletSolde, walletDevise)} — recharge, retrait Mobile Money ou transfert.`
      : `Solde disponible : ${formatMoney(walletSolde, walletDevise)} — retrait Mobile Money.`;
  const bodyGridClass =
    actionCount <= 1
      ? 'grid gap-4 grid-cols-1'
      : actionCount === 2
        ? 'grid gap-4 md:grid-cols-2'
        : 'grid gap-4 md:grid-cols-2 lg:grid-cols-3';

  return (
    <section className="apprenant-parrainage-panel" aria-labelledby="wallet-actions-heading">
      <header className="apprenant-parrainage-panel__header">
        <span className="apprenant-parrainage-panel__header-icon" aria-hidden>
          <LucideSymbolIcon name="account_balance_wallet" tone="inherit" size={22} />
        </span>
        <div className="min-w-0">
          <h2 id="wallet-actions-heading" className="apprenant-parrainage-panel__title">
            {heading}
          </h2>
          <p className="apprenant-parrainage-panel__subtitle">{subtitle ?? defaultSubtitle}</p>
        </div>
      </header>
      <div className={`apprenant-parrainage-panel__body ${bodyGridClass}`}>
        {showRecharge ? (
          <div className="rounded-xl border border-outline-variant/25 bg-surface-container-low p-4">
            <p className="text-sm font-bold text-slate-900">Recharger</p>
            <p className="mt-1 text-xs text-on-surface-variant">Via Mobile Money (FlexPay).</p>
            <label className="mt-3 block text-xs font-bold text-on-surface-variant">
              Montant (CDF)
              <input
                type="number"
                min={0}
                className="mt-1 w-full rounded-lg border border-outline-variant/30 px-3 py-2 text-sm"
                value={rechargeAmount}
                onChange={(e) => setRechargeAmount(e.target.value)}
              />
            </label>
            <label className="mt-2 block text-xs font-bold text-on-surface-variant">
              Téléphone source
              <input
                type="tel"
                className="mt-1 w-full rounded-lg border border-outline-variant/30 px-3 py-2 text-sm"
                value={rechargePhone}
                onChange={(e) => setRechargePhone(e.target.value)}
                placeholder="+243…"
              />
            </label>
            <button
              type="button"
              disabled={busy != null}
              className="apprenant-parrainage__btn apprenant-parrainage__btn--primary mt-3 w-full"
              onClick={() => void handleRecharge()}
            >
              Recharger le portefeuille
            </button>
          </div>
        ) : null}

        <div className="rounded-xl border border-outline-variant/25 bg-surface-container-low p-4">
          <p className="text-sm font-bold text-slate-900">Retrait électronique</p>
          <p className="mt-1 text-xs text-on-surface-variant">Vers votre numéro Mobile Money.</p>
          <label className="mt-3 block text-xs font-bold text-on-surface-variant">
            Montant (CDF)
            <input
              type="number"
              min={0}
              className="mt-1 w-full rounded-lg border border-outline-variant/30 px-3 py-2 text-sm"
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(e.target.value)}
            />
          </label>
          <label className="mt-2 block text-xs font-bold text-on-surface-variant">
            Téléphone destination
            <input
              type="tel"
              className="mt-1 w-full rounded-lg border border-outline-variant/30 px-3 py-2 text-sm"
              value={withdrawPhone}
              onChange={(e) => setWithdrawPhone(e.target.value)}
              placeholder="+243…"
            />
          </label>
          <button
            type="button"
            disabled={busy != null}
            className="apprenant-parrainage__btn apprenant-parrainage__btn--primary mt-3 w-full"
            onClick={() => void handleWithdraw()}
          >
            Demander un retrait
          </button>
        </div>

        {showTransfer ? (
          <div className="rounded-xl border border-outline-variant/25 bg-surface-container-low p-4">
            <p className="text-sm font-bold text-slate-900">Transférer des bonus</p>
            <p className="mt-1 text-xs text-on-surface-variant">
              Transférez vos points parrainage à un autre membre via son code parrainage (transfert interne
              plateforme).
            </p>
            <label className="mt-3 block text-xs font-bold text-on-surface-variant">
              Montant (CDF)
              <input
                type="number"
                min={0}
                max={transferable > 0 ? transferable : undefined}
                className="mt-1 w-full rounded-lg border border-outline-variant/30 px-3 py-2 text-sm"
                value={transferAmount}
                onChange={(e) => setTransferAmount(e.target.value)}
              />
            </label>
            <label className="mt-2 block text-xs font-bold text-on-surface-variant">
              Code de parrainage du destinataire
              <input
                type="text"
                className="mt-1 w-full rounded-lg border border-outline-variant/30 px-3 py-2 text-sm uppercase"
                value={transferReferralCode}
                onChange={(e) => setTransferReferralCode(e.target.value.toUpperCase())}
                placeholder="Ex. JCA-ABC123"
                autoCapitalize="characters"
                spellCheck={false}
              />
            </label>
            <button
              type="button"
              disabled={busy != null}
              className="apprenant-parrainage__btn apprenant-parrainage__btn--outline mt-3 w-full"
              onClick={() => void handleTransfer()}
            >
              Transférer les bonus
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
